import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getRestaurants, getAnalytics, getDashboardStats } from '../lib/api';
import { Store, Eye, Utensils, Plus, TrendingUp, ArrowRight, Sparkles, Rocket, ShoppingCart, DollarSign, Clock, BarChart3 } from 'lucide-react';

export default function Dashboard() {
    const [stats, setStats] = useState({ restaurants: 0, views: 0, dishes: 0 });
    const [quickStats, setQuickStats] = useState({ today_orders: 0, today_revenue: 0, pending_orders: 0, weekly_trend: [], recent_orders: [] });
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [restaurantsRes, analyticsRes, dashStatsRes] = await Promise.all([
                getRestaurants(),
                getAnalytics(),
                getDashboardStats()
            ]);
            setRestaurants(restaurantsRes.data.slice(0, 5));
            setStats({
                restaurants: analyticsRes.data.total_restaurants,
                views: analyticsRes.data.total_views,
                dishes: analyticsRes.data.total_dishes
            });
            setQuickStats(dashStatsRes.data);
        } catch {
            // Dashboard data fetch failed
        } finally {
            setLoading(false);
        }
    }, []);

    // Re-fetch on every navigation (including back button)
    useEffect(() => {
        fetchData();
    }, [fetchData, location.key]);

    const isNewUser = !loading && restaurants.length <= 1;
    const maxTrend = Math.max(...(quickStats.weekly_trend?.map(d => d.orders) || [1]), 1);

    return (
        <DashboardLayout>
            <div className="space-y-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold text-[#2B2A27] tracking-tight">Dashboard</h1>
                        <p className="text-[#69665B] mt-1">Welcome back! Here's an overview of your menus.</p>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/setup">
                            <Button className="bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full gap-2" data-testid="start-wizard-btn">
                                <Sparkles className="w-4 h-4" />
                                Quick Setup
                            </Button>
                        </Link>
                        <Link to="/dashboard/restaurants/new">
                            <Button variant="outline" className="rounded-full gap-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10" data-testid="create-restaurant-btn">
                                <Plus className="w-4 h-4" />
                                Manual Setup
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Onboarding Card for new users */}
                {isNewUser && (
                    <Card className="border-[#C9A227]/30 bg-gradient-to-br from-[#C9A227]/5 to-transparent">
                        <CardContent className="p-8">
                            <div className="flex flex-col md:flex-row items-center gap-6">
                                <div className="w-20 h-20 bg-[#C9A227]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                                    <Rocket className="w-10 h-10 text-[#C9A227]" />
                                </div>
                                <div className="flex-1 text-center md:text-left">
                                    <h2 className="text-xl font-semibold text-[#2B2A27] mb-2">Welcome to AR Menu Studio!</h2>
                                    <p className="text-[#69665B] mb-4">
                                        Create stunning AR menus for your restaurant clients in minutes.
                                    </p>
                                    <Link to="/setup">
                                        <Button className="bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full gap-2">
                                            <Sparkles className="w-4 h-4" />
                                            Start Creating Your First Menu
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Quick Stats - Today's Performance */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="quick-stats-section">
                    <Card className="border-[#E5E1D1] shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-[#C9A227]/5 to-transparent">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-[#69665B] uppercase tracking-wider font-medium">Today's Orders</p>
                                    <p className="text-3xl font-bold text-[#2B2A27] mt-1" data-testid="today-orders-count">
                                        {loading ? '...' : quickStats.today_orders}
                                    </p>
                                </div>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#C9A227]/10">
                                    <ShoppingCart className="w-6 h-6 text-[#C9A227]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-[#E5E1D1] shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-[#4F7C61]/5 to-transparent">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-[#69665B] uppercase tracking-wider font-medium">Today's Revenue</p>
                                    <p className="text-3xl font-bold text-[#2B2A27] mt-1" data-testid="today-revenue">
                                        {loading ? '...' : `QAR ${quickStats.today_revenue.toLocaleString()}`}
                                    </p>
                                </div>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#4F7C61]/10">
                                    <DollarSign className="w-6 h-6 text-[#4F7C61]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-[#E5E1D1] shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-[#D2924E]/5 to-transparent">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-[#69665B] uppercase tracking-wider font-medium">Pending Orders</p>
                                    <p className="text-3xl font-bold text-[#2B2A27] mt-1" data-testid="pending-orders-count">
                                        {loading ? '...' : quickStats.pending_orders}
                                    </p>
                                </div>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D2924E]/10">
                                    <Clock className="w-6 h-6 text-[#D2924E]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-[#E5E1D1] shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                            <p className="text-xs text-[#69665B] uppercase tracking-wider font-medium mb-3">7-Day Trend</p>
                            <div className="flex items-end gap-1 h-10" data-testid="weekly-trend-chart">
                                {(quickStats.weekly_trend || []).map((d, i) => (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                        <div
                                            className="w-full rounded-t-sm transition-all duration-300"
                                            style={{
                                                height: `${Math.max((d.orders / maxTrend) * 32, 2)}px`,
                                                backgroundColor: i === (quickStats.weekly_trend?.length - 1) ? '#C9A227' : '#E5E1D1'
                                            }}
                                        />
                                        <span className="text-[9px] text-[#969282]">{d.date}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { label: 'Total Restaurants', value: stats.restaurants, icon: Store, color: '#C9A227' },
                        { label: 'Total Menu Views', value: stats.views, icon: Eye, color: '#4F7C61' },
                        { label: 'Total Dishes', value: stats.dishes, icon: Utensils, color: '#D2924E' },
                    ].map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <Card key={stat.label} className="border-[#E5E1D1] shadow-sm hover:shadow-md transition-shadow">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-[#69665B]">{stat.label}</p>
                                            <p className="text-3xl font-semibold text-[#2B2A27] mt-1">
                                                {loading ? '...' : stat.value.toLocaleString()}
                                            </p>
                                        </div>
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: `${stat.color}15` }}
                                        >
                                            <Icon className="w-6 h-6" style={{ color: stat.color }} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Recent Orders + Recent Restaurants side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Orders */}
                    <Card className="border-[#E5E1D1] shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between pb-3">
                            <CardTitle className="text-lg font-semibold text-[#2B2A27]">Recent Orders</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => <div key={i} className="h-14 bg-[#F0EDDF] rounded-xl animate-pulse" />)}
                                </div>
                            ) : (quickStats.recent_orders || []).length === 0 ? (
                                <div className="text-center py-8 text-[#69665B]">
                                    <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-[#969282]" />
                                    <p className="text-sm">No orders yet</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {(quickStats.recent_orders || []).map((order) => (
                                        <div key={order.id} className="flex items-center justify-between p-3 bg-[#F0EDDF]/50 rounded-xl" data-testid={`recent-order-${order.id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${
                                                    order.status === 'new' ? 'bg-blue-500' :
                                                    order.status === 'preparing' ? 'bg-amber-500' :
                                                    order.status === 'ready' ? 'bg-green-500' :
                                                    'bg-gray-400'
                                                }`} />
                                                <div>
                                                    <p className="text-sm font-medium text-[#2B2A27]">#{order.id}</p>
                                                    <p className="text-xs text-[#969282]">{order.restaurant_name || 'Unknown'}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-[#C9A227]">QAR {order.total}</p>
                                                <p className="text-xs text-[#969282] capitalize">{order.status}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Restaurants */}
                    <Card className="border-[#E5E1D1] shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between pb-3">
                            <CardTitle className="text-lg font-semibold text-[#2B2A27]">Recent Restaurants</CardTitle>
                            <Link to="/dashboard/restaurants" className="text-[#C9A227] hover:text-[#B8922A] text-sm font-medium flex items-center gap-1">
                                View all <ArrowRight className="w-4 h-4" />
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => <div key={i} className="h-14 bg-[#F0EDDF] rounded-xl animate-pulse" />)}
                                </div>
                            ) : restaurants.length === 0 ? (
                                <div className="text-center py-8">
                                    <Store className="w-10 h-10 text-[#969282] mx-auto mb-3" />
                                    <p className="text-sm text-[#69665B] mb-3">No restaurants yet</p>
                                    <Link to="/setup">
                                        <Button className="bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full text-sm">
                                            Create your first restaurant
                                        </Button>
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {restaurants.map((restaurant) => (
                                        <Link
                                            key={restaurant.id}
                                            to={`/dashboard/restaurants/${restaurant.id}`}
                                            className="flex items-center justify-between p-3 bg-[#F0EDDF]/50 rounded-xl hover:bg-[#F0EDDF] transition-colors"
                                            data-testid={`restaurant-item-${restaurant.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                                    style={{ backgroundColor: restaurant.primary_color || '#C9A227' }}
                                                >
                                                    <Store className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-[#2B2A27]">{restaurant.name}</p>
                                                    <p className="text-xs text-[#969282]">/menu/{restaurant.slug}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    restaurant.status === 'published' ? 'badge-published' : 'badge-draft'
                                                }`}>{restaurant.status}</span>
                                                <div className="flex items-center gap-1 text-[#69665B] text-xs">
                                                    <TrendingUp className="w-3.5 h-3.5" />
                                                    {restaurant.views || 0}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
}
